import {
  FcArea,
  FcCenterText,
  FcColorType,
  FcGrid,
  FcGridImageData,
  FcLogo,
  FcOverlay,
  FcPoint,
  FcPolygon,
  FcReservedArea,
  FcSvg,
  fcBuildSvgColor,
  fcBuildSvgColorForImageFill,
  getRotatedQuad
} from '@dtx-company/flowcode-generator-types/src'
import { FcGridRenderer } from './fc_grid_renderer'
import {
  FcPixelData,
  fcGetPixelData,
  fcGetPixelDataRaw,
  fcImageAddSize,
  fcImageToTag
} from './fc_images'
import { fcAddClipPath } from './fc_container_renderer'
import { fcGetDomId } from './fc_dom'
import { fcRenderCenterText } from './fc_fonts'
import { roundDecimal } from './fc_utils'

export async function fcRenderDataGrid(
  dataGrid: FcGrid,
  svg: FcSvg,
  reserved: FcReservedArea,
  sizePadding: number,
  logo?: FcLogo,
  centerText?: FcCenterText,
  radius?: number,
  inset?: boolean,
  overlays?: FcOverlay[],
  overlayMargin?: number,
  overlayRadiusMask?: number,
  dataCheckOnly?: boolean,
  maskStr?: string
): Promise<void> {
  let blendData: FcGridImageData | undefined = undefined
  if (logo && logo.imgUrl) {
    blendData = await renderLogo(dataGrid, logo, svg, reserved, dataCheckOnly)
  }

  const overlayBlendData: FcGridImageData[] = []
  if (overlays !== undefined && overlays.length) {
    if (overlayRadiusMask) {
      const idBase = fcGetDomId()
      const w = svg.width
      const h = svg.height
      const r = (svg.width * overlayRadiusMask) / 200
      let element = `<circle id="${idBase}containerShape" cx="${roundDecimal(
        w / 2
      )}" cy="${roundDecimal(h / 2)}" r="${roundDecimal(r)}" ${fcBuildSvgColor(
        '#ffff0000',
        FcColorType.STROKE,
        false
      )} stroke-width="0" ${fcBuildSvgColor('#ffff0000', FcColorType.FILL, svg.getUseCmyk())} />`
      element += fcAddClipPath(idBase)
      element += `<g clip-path="url(#${idBase}clipPath)">`
      svg.addToDom(element)
    }
    const len = overlays.length
    for (let i = 0; i < len; i++) {
      const imgData = await renderOverlay(dataGrid, overlays[i], svg, reserved, overlayMargin || 0)
      if (imgData) {
        overlayBlendData.push(imgData)
      }
    }
    if (overlayRadiusMask) {
      svg.addToDom('</g>')
    }
  }
  let textBlendData: FcGridImageData | undefined = undefined
  if (centerText !== undefined) {
    if (centerText.blend) {
      textBlendData = {
        blend: true,
        blendModuleColorFg: centerText.blendModuleColorFg,
        blendModuleColorBg: centerText.blendModuleColorBg,
        blendModuleShape: centerText.blendModuleShape,
        blendModuleSize: centerText.blendModuleSize,
        blendErase: centerText.blendErase,
        blendMask: new FcReservedArea(),
        blendModuleTolerationFg: 0,
        blendModuleTolerationBg: 0,
        blendModulePixelsToCheck: 2,
        rawImage: new FcArea(0, 0, 0, 0)
      }
    }
    await fcRenderCenterText(centerText, svg, textBlendData ? textBlendData.blendMask! : reserved)
    if (textBlendData && centerText.blendErase) {
      const textSvg = new FcSvg(0, 0, svg.width, svg.height)
      await fcRenderCenterText(centerText, textSvg, new FcReservedArea())
      const svgData = textSvg.getDom()
      const pixelData = await fcGetPixelDataRaw(svgData, textSvg.width, textSvg.height)
      textBlendData.rawImage = new FcArea(0, 0, textSvg.width, textSvg.height, pixelData.pixels)
    }
  }

  if (maskStr) {
    svg.addToDom(maskStr)
  }
  await new FcGridRenderer(
    dataGrid,
    sizePadding,
    blendData,
    overlayBlendData,
    textBlendData,
    logo?.inCode,
    logo?.inCodeBitmap
  ).renderModules(svg, reserved, radius, inset, dataCheckOnly)
}

async function renderLogo(
  dataGrid: FcGrid,
  logo: FcLogo,
  svg: FcSvg,
  reserved: FcReservedArea,
  dataCheckOnly?: boolean
): Promise<FcGridImageData | undefined> {
  let imageData
  let gridLogoData: FcGridImageData | undefined = undefined
  // NOTE: This try/catch was existing but is flagged by updated eslint rules
  // because there are no tests, let's keep the try/catch. Come back and add
  // tests, pretty please.
  // eslint-disable-next-line no-useless-catch
  try {
    imageData = await fcImageToTag(
      svg.getUseCmyk(),
      logo.imgUrl,
      logo.fillColor ? fcBuildSvgColorForImageFill(logo.fillColor, svg.getUseCmyk()) : undefined
    )
  } catch (err) {
    throw err
  }

  // only center for now
  const svgWidth = svg.width
  const svgHeight = svg.height

  // subtract another 5 to make it more compatible with previous codebase
  const perc = (dataGrid.dataGridPercentage - 5) / 100
  let fw = ((logo.width * svgWidth) / 100) * perc
  let fh = ((logo.height * svgHeight) / 100) * perc

  let pixels: number[][] | undefined = undefined
  let rawPixels: number[][] | undefined = undefined
  let padding: number | undefined = undefined
  if (logo.blend || logo.logoModulePadding !== undefined) {
    padding = logo.logoModulePadding || 0
  }

  if (logo.inCode) {
    logo.preserveAspect = true
  }

  if (padding !== undefined || logo.preserveAspect || logo.rotate) {
    // NOTE: This try/catch was existing but is flagged by updated eslint rules
    // because there are no tests, let's keep the try/catch. Come back and add
    // tests, pretty please.
    // eslint-disable-next-line no-useless-catch
    try {
      let url = logo.imgUrl
      let isMask = false
      if (logo.mask) {
        isMask = true
        url = logo.mask
      }
      const pixelData = await fcGetPixelData(
        url,
        isMask,
        dataCheckOnly ? 50 : fw,
        dataCheckOnly ? 50 : fh
      )
      if (padding !== undefined) {
        pixels = pixelData.pixels
      }
      const ratio = pixelData.width / pixelData.height
      const oldRatio = fw / fh

      // preserve aspect ratio
      // To not break old codes, only resize ratio for blend, preserveAspect, rotate
      if ((logo.preserveAspect || logo.blend) && ratio !== oldRatio) {
        if (logo.width > logo.height) {
          logo.height = logo.width / ratio
        } else {
          logo.width = ratio * logo.height
        }
        svg.logoNewWidth = logo.width
        svg.logoNewHeight = logo.height
        fw = ((logo.width * svgWidth) / 100) * perc
        fh = ((logo.height * svgHeight) / 100) * perc
      }
    } catch (err) {
      throw err
    }
  }

  const fx = (svgWidth - fw) / 2
  const fy = (svgHeight - fh) / 2

  if (logo.blend || dataGrid.erasedExact || logo.inCode) {
    // NOTE: This try/catch was existing but is flagged by updated eslint rules
    // because there are no tests, let's keep the try/catch. Come back and add
    // tests, pretty please.
    // eslint-disable-next-line no-useless-catch
    try {
      const rawImage = await fcGetPixelDataRaw(logo.imgUrl, fw, fh)
      rawPixels = rawImage.pixels
    } catch (err) {
      throw err
    }
  }

  let rotate: string | undefined = undefined
  if (logo.rotate) {
    if (!logo.inCodePixels) {
      rotate = `transform="rotate(${roundDecimal(logo.rotate)},${roundDecimal(
        fx + fw / 2
      )},${roundDecimal(fy + fh / 2)})"`
      svg.addToDom(`<g ${rotate}>`)
    }
  }
  if (!logo.inCodePixels) {
    const logoImg = fcImageAddSize(imageData, fx, fy, fw, fh, '')
    svg.addToDom(logoImg)
  }
  let box: FcArea
  let polygon: FcPolygon | undefined = undefined
  let angle: number | undefined = undefined
  const outerPadding = pixels ? 0 : logo.outerPadding || 0
  const fxr = fx - outerPadding
  const fyr = fy - outerPadding
  const fwr = fw + 2 * outerPadding
  const fhr = fh + 2 * outerPadding
  if (rotate) {
    if (!logo.inCodePixels) {
      svg.addToDom(`</g>`)
    }
    angle = (logo.rotate! * Math.PI) / 180
    const points = getRotatedQuad(
      new FcArea(fxr, fyr, fwr, fhr),
      new FcPoint(fx + fwr / 2, fy + fhr / 2),
      angle,
      1
    )
    polygon = new FcPolygon([points[0], points[1], points[2], points[3]])
    box = new FcArea(fxr, fyr, fwr, fhr, pixels, padding, angle, polygon)
  } else {
    box = new FcArea(fxr, fyr, fwr, fhr, pixels, padding)
  }
  if (!logo.inCodePixels) {
    reserved.addBoxArea(box)
  }
  if (logo.blend) {
    gridLogoData = {
      blend: true,
      blendModuleSize: logo.blendModuleSize,
      blendModuleColorFg: logo.blendModuleColorFg,
      blendModuleColorBg: logo.blendModuleColorBg,
      blendModuleShape: logo.blendModuleShape,
      blendModuleImageUrl: logo.blendModuleImageUrl,
      blendModuleTolerationFg: logo.blendModuleTolerationFg,
      blendModuleTolerationBg: logo.blendModuleTolerationBg,
      blendModulePixelsToCheck: logo.blendModulePixelsToCheck,
      rotate: logo.rotate,
      blendErase: logo.blendErase,
      rawImage: new FcArea(fx, fy, fw, fh, rawPixels, 0, angle, polygon)
    }
    const blendMask = new FcReservedArea()
    blendMask.addBoxArea(box)
    gridLogoData.blendMask = blendMask
  } else if (dataGrid.erasedExact || logo.inCode) {
    gridLogoData = {
      blend: false,
      rotate: logo.rotate,
      blendModulePixelsToCheck: 0,
      rawImage: new FcArea(fx, fy, fw, fh, rawPixels, 0, angle, polygon)
    }
  }
  return gridLogoData
}

export async function fcGetOverlayMargin(
  dataGrid: FcGrid,
  overlays: FcOverlay[],
  svgWidth: number,
  svgHeight: number
): Promise<number> {
  let margin = 0
  const len = overlays.length
  for (let i = 0; i < len; i++) {
    const overlay = overlays[i]
    if (overlay.clip) {
      continue
    }
    let pixelData: FcPixelData
    // NOTE: This try/catch was existing but is flagged by updated eslint rules
    // because there are no tests, let's keep the try/catch. Come back and add
    // tests, pretty please.
    // eslint-disable-next-line no-useless-catch
    try {
      pixelData = await fcGetPixelData(overlay.imgUrl, false, overlay.width, 0)
    } catch (err) {
      throw err
    }
    const ratio = pixelData.width / pixelData.height
    const fw = (overlay.width * svgWidth) / 100
    const fh = ((overlay.width / ratio) * svgHeight) / 100
    const radians = (Math.PI * 3) / 2 + (overlay.degrees * Math.PI) / 180
    const radius = (overlay.radius * (svgWidth / 2)) / 100
    const fx = svgWidth / 2 + radius * Math.cos(radians) - fw / 2
    const fy = svgHeight / 2 + radius * Math.sin(radians) - fh / 2
    let overlap = fx + fw - svgWidth
    if (overlap > 0 && overlap > margin) {
      margin = overlap
    }
    overlap = fy + fh - svgHeight
    if (overlap > 0 && overlap > margin) {
      margin = overlap
    }
    overlap = -fx
    if (overlap > 0 && overlap > margin) {
      margin = overlap
    }
    overlap = -fy
    if (overlap > 0 && overlap > margin) {
      margin = overlap
    }
  }
  return margin
}

async function renderOverlay(
  dataGrid: FcGrid,
  overlay: FcOverlay,
  svg: FcSvg,
  reserved: FcReservedArea,
  overlayMargin: number
): Promise<FcGridImageData | undefined> {
  let imageData
  let gridOverlayData: FcGridImageData | undefined = undefined
  // NOTE: This try/catch was existing but is flagged by updated eslint rules
  // because there are no tests, let's keep the try/catch. Come back and add
  // tests, pretty please.
  // eslint-disable-next-line no-useless-catch
  try {
    imageData = await fcImageToTag(
      svg.getUseCmyk(),
      overlay.imgUrl,
      overlay.fillColor
        ? fcBuildSvgColorForImageFill(overlay.fillColor, svg.getUseCmyk())
        : undefined
    )
  } catch (err) {
    throw err
  }

  const svgWidth = svg.width
  const svgHeight = svg.height
  let pixels: number[][] | undefined = undefined
  let rawPixels: number[][] | undefined = undefined
  let url = overlay.imgUrl
  let isMask = false
  if (overlay.maskUrl) {
    isMask = true
    url = overlay.maskUrl
  }
  let pixelData: FcPixelData
  // NOTE: This try/catch was existing but is flagged by updated eslint rules
  // because there are no tests, let's keep the try/catch. Come back and add
  // tests, pretty please.
  // eslint-disable-next-line no-useless-catch
  try {
    pixelData = await fcGetPixelData(url, isMask, overlay.width, 0)
  } catch (err) {
    throw err
  }
  pixels = pixelData.pixels
  const ratio = pixelData.width / pixelData.height
  const fw = (overlay.width * svgWidth) / 100
  const fh = ((overlay.width / ratio) * svgHeight) / 100
  const radius = (overlay.radius * (svgWidth / 2)) / 100
  const radians = (Math.PI * 3) / 2 + (overlay.degrees * Math.PI) / 180
  const fx = svgWidth / 2 + radius * Math.cos(radians) - fw / 2
  const fy = svgHeight / 2 + radius * Math.sin(radians) - fh / 2

  if (overlay.blend || dataGrid.erasedExact) {
    // NOTE: This try/catch was existing but is flagged by updated eslint rules
    // because there are no tests, let's keep the try/catch. Come back and add
    // tests, pretty please.
    // eslint-disable-next-line no-useless-catch
    try {
      const rawImage = await fcGetPixelDataRaw(overlay.imgUrl, fw, fh)
      rawPixels = rawImage.pixels
    } catch (err) {
      throw err
    }
  }

  let rotate = ''
  let rotateEnd = ''
  if (overlay.rotate) {
    if (overlay.clip) {
      rotate = `<g transform="rotate(${roundDecimal(overlay.rotate)},${roundDecimal(
        fx + fw / 2
      )},${roundDecimal(fy + fh / 2)})">`
    } else {
      rotate = `<g transform="rotate(${roundDecimal(overlay.rotate)},${roundDecimal(
        fx + overlayMargin + fw / 2
      )},${roundDecimal(fy + overlayMargin + fh / 2)})">`
    }
    rotateEnd = '</g>'
  }
  if (overlay.clip) {
    const overlayImg = rotate + fcImageAddSize(imageData, fx, fy, fw, fh, '') + rotateEnd
    svg.addToDom(overlayImg)
  } else {
    const overlayImg =
      rotate +
      fcImageAddSize(imageData, fx + overlayMargin, fy + overlayMargin, fw, fh, '') +
      rotateEnd
    svg.addOverlay(overlayImg)
  }
  let polygon: FcPolygon | undefined = undefined
  let angle: number | undefined = undefined
  let box: FcArea
  if (overlay.rotate) {
    angle = (overlay.rotate! * Math.PI) / 180
    const points = getRotatedQuad(
      new FcArea(fx, fy, fw, fh),
      new FcPoint(fx + fw / 2, fy + fh / 2),
      angle,
      1
    )
    polygon = new FcPolygon([points[0], points[1], points[2], points[3]])
    box = new FcArea(fx, fy, fw, fh, pixels, overlay.padding, angle, polygon)
  } else {
    box = new FcArea(fx, fy, fw, fh, pixels, overlay.padding)
  }
  reserved.addBoxArea(box)

  if (overlay.blend) {
    gridOverlayData = {
      blend: true,
      blendModuleSize: overlay.blendModuleSize,
      blendModuleColorFg: overlay.blendModuleColorFg,
      blendModuleColorBg: overlay.blendModuleColorBg,
      blendModuleShape: overlay.blendModuleShape,
      blendModuleImageUrl: overlay.blendModuleImageUrl,
      blendModuleTolerationFg: overlay.blendModuleTolerationFg,
      blendModuleTolerationBg: overlay.blendModuleTolerationBg,
      blendModulePixelsToCheck: overlay.blendModulePixelsToCheck,
      blendErase: overlay.blendErase,
      rotate: overlay.rotate,
      rawImage: new FcArea(fx, fy, fw, fh, rawPixels, 0, angle, polygon)
    }
    const blendMask = new FcReservedArea()
    blendMask.addBoxArea(box)
    gridOverlayData.blendMask = blendMask
  } else if (dataGrid.erasedExact) {
    gridOverlayData = {
      blend: false,
      rotate: overlay.rotate,
      rawImage: new FcArea(fx, fy, fw, fh, rawPixels, 0, angle, polygon)
    }
  }
  return gridOverlayData
}
